/**
 * useFileUpload.js
 *
 * Manages a single file-upload field used within the quote form. Each
 * instance owns one state slot ({ file, uploaded, isUploading, uploadError })
 * and a localStorage entry under `quoteFormUploads[storageKey]`.
 *
 * It encapsulates the four boilerplate concerns each upload field shared
 * before this refactor:
 *
 *   1. Initialize state from localStorage on mount.
 *   2. Run the upload when the user picks a file (gated by `canUpload`).
 *   3. Persist the successful upload result to localStorage and clear it
 *      from there on cleanup or failure.
 *   4. Re-attempt the upload at submit time if the user picked a file but
 *      the interactive upload was skipped (e.g. preconditions weren't met).
 *
 * The returned `handleSelect`, `clear`, and `uploadIfNeeded` callbacks
 * have stable identities across renders. They read the latest options
 * (uploadFn, canUpload, etc.) via a ref, so closures over `formData`
 * stay current without forcing the caller to memoize.
 *
 * Example:
 *
 *   const taxFile = useFileUpload({
 *     storageKey: 'taxCert',
 *     uploadFn: (file) => uploadTaxCert({ file, email: formData.email, customer: {...} }),
 *     parseResponse: (d) => ({ fileGid: d?.fileGid, fileUrl: d?.fileUrl, customerId: d?.customerId, customerGid: d?.customerGid }),
 *     canUpload: () => formData.tax_exempt === 'Yes' && !!formData.email,
 *     errorMessage: 'Tax certificate upload failed.',
 *   });
 *
 *   // In the file picker:
 *   <FileInput onFileSelect={taxFile.handleSelect} />
 *
 *   // When a sibling form value invalidates the upload (e.g. tax_exempt → No):
 *   taxFile.clear();
 *
 *   // At submit time:
 *   const result = await taxFile.uploadIfNeeded();
 *   //   - returns the existing `uploaded` payload if already uploaded
 *   //   - uploads now and returns the parsed response if a file is selected
 *   //   - returns null if no file is selected
 *   //   - throws on upload error (err.uploadErrors is the array from the API)
 */

import { useCallback, useRef, useState } from 'react';

const UPLOADS_KEY = 'quoteFormUploads';

const EMPTY_STATE = Object.freeze({
  file: null,
  uploaded: null,
  isUploading: false,
  uploadError: null,
});

function loadUploads() {
  try {
    const raw = localStorage.getItem(UPLOADS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveUploads(data) {
  try {
    localStorage.setItem(UPLOADS_KEY, JSON.stringify(data));
  } catch {}
}

function clearPersisted(key) {
  const uploads = loadUploads();
  if (!uploads?.[key]) return;
  const next = { ...uploads };
  delete next[key];
  saveUploads(next);
}

function persist(key, value) {
  const uploads = loadUploads();
  saveUploads({ ...uploads, [key]: value });
}

export default function useFileUpload({
  storageKey,
  uploadFn,
  parseResponse = (d) => d,
  canUpload,
  errorMessage = 'Upload failed.',
}) {
  const [state, setState] = useState(() => ({
    ...EMPTY_STATE,
    uploaded: loadUploads()?.[storageKey] ?? null,
  }));

  // Latest options — refreshed every render so stable callbacks below
  // always read current closures (e.g. canUpload that depends on formData).
  const optsRef = useRef({ uploadFn, parseResponse, canUpload, errorMessage });
  optsRef.current = { uploadFn, parseResponse, canUpload, errorMessage };

  // Latest state — for uploadIfNeeded reads at submit time.
  const stateRef = useRef(state);
  stateRef.current = state;

  const clear = useCallback(() => {
    setState({ ...EMPTY_STATE });
    clearPersisted(storageKey);
  }, [storageKey]);

  /**
   * Internal: actually run the upload and update state. Caller is
   * responsible for any precondition gating.
   *
   * Throws on failure. The thrown Error carries `.uploadErrors`
   * (array from the API) when available, so the submit hook can
   * surface those without wrapping the message.
   */
  const runUpload = useCallback(async (file) => {
    setState((prev) => ({ ...prev, file, isUploading: true, uploadError: null, uploaded: null }));

    let data;
    let errors;
    try {
      ({ data, errors } = await optsRef.current.uploadFn(file));
    } catch (err) {
      const msg = err?.message || optsRef.current.errorMessage;
      setState((prev) => ({ ...prev, isUploading: false, uploaded: null, uploadError: msg }));
      const wrapped = err instanceof Error ? err : new Error(msg);
      throw wrapped;
    }

    if (errors && errors.length) {
      const msg = errors[0] || optsRef.current.errorMessage;
      setState((prev) => ({ ...prev, isUploading: false, uploaded: null, uploadError: msg }));
      const e = new Error(msg);
      e.uploadErrors = errors;
      throw e;
    }

    const normalized = optsRef.current.parseResponse(data);
    setState({ ...EMPTY_STATE, file, uploaded: normalized });
    persist(storageKey, normalized);
    return normalized;
  }, [storageKey]);

  /**
   * Called from the file picker. Clears state if `file` is null/empty;
   * otherwise stores the file and (subject to `canUpload`) attempts the
   * upload immediately. Errors are captured into `uploadError` state and
   * NOT rethrown — the picker UX shouldn't have to await this.
   */
  const handleSelect = useCallback(async (file) => {
    if (!file) {
      clear();
      return null;
    }

    const allowUpload = optsRef.current.canUpload ? optsRef.current.canUpload() : true;
    if (!allowUpload) {
      // Hold the file in state without uploading yet. The user can flip
      // the gating condition later, then submit, and uploadIfNeeded()
      // will pick it up.
      setState({ ...EMPTY_STATE, file });
      clearPersisted(storageKey);
      return null;
    }

    try {
      return await runUpload(file);
    } catch {
      return null;
    }
  }, [clear, runUpload, storageKey]);

  /**
   * Submit-time upload. If the upload already happened interactively,
   * returns the cached payload. If a file is selected but not yet
   * uploaded, runs the upload now. If no file is selected, returns null.
   *
   * Throws on upload failure so the submit flow can stop and surface
   * `.uploadErrors`.
   */
  const uploadIfNeeded = useCallback(async () => {
    const cur = stateRef.current;
    if (cur.uploaded?.fileGid) return cur.uploaded;
    if (cur.file) return await runUpload(cur.file);
    return null;
  }, [runUpload]);

  return {
    file: state.file,
    uploaded: state.uploaded,
    isUploading: state.isUploading,
    uploadError: state.uploadError,
    handleSelect,
    clear,
    uploadIfNeeded,
  };
}
